// Cache Service per gestire la persistenza dei dati tramite IndexedDB
const DB_NAME = 'ARShadesStudioCache';
const DB_VERSION = 1;
const PRODUCTS_STORE = 'products';
const VARIANTS_STORE = 'variants';
const BRANDS_STORE = 'brands';
const METADATA_STORE = 'metadata';

// Configurazione TTL (Time To Live) in millisecondi
const CACHE_TTL = {
    PRODUCTS: 60 * 60 * 1000, // 1 ora
    VARIANTS: 2 * 60 * 60 * 1000, // 2 ore
    BRANDS: 24 * 60 * 60 * 1000, // 24 ore
};

// Funzione per sanificare i dati prima della cache
const sanitizeDataForCache = (data) => {
    if (data === null || data === undefined) {
        return data;
    }

    const dataType = typeof data;

    // Tipi primitivi sono già serializzabili
    if (dataType === 'string' || dataType === 'number' || dataType === 'boolean') {
        return data;
    }

    // Array
    if (Array.isArray(data)) {
        return data.map(item => sanitizeDataForCache(item)).filter(item => item !== undefined);
    }

    // Date objects
    if (data instanceof Date) {
        return data.toISOString();
    }

    // Firestore Timestamp objects
    if (data && typeof data.toDate === 'function') {
        try {
            return data.toDate().toISOString();
        } catch (error) {
            console.warn('Failed to convert Firestore timestamp:', error);
            return null;
        }
    }

    // Objects
    if (dataType === 'object' && data.constructor === Object) {
        const sanitizedObj = {};

        for (const [key, value] of Object.entries(data)) {
            const sanitizedValue = sanitizeDataForCache(value);
            if (sanitizedValue !== undefined) {
                sanitizedObj[key] = sanitizedValue;
            }
        }

        return sanitizedObj;
    }

    // Per altri tipi (funzioni, simboli, etc.), restituisci undefined per filtrarli
    console.warn('Skipping non-serializable data type:', dataType, data);
    return undefined;
};

// Inizializza il database
const initializeDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            console.error('Error opening IndexedDB:', event.target.error);
            reject(event.target.error);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;

            // Store per i prodotti
            if (!db.objectStoreNames.contains(PRODUCTS_STORE)) {
                const productsStore = db.createObjectStore(PRODUCTS_STORE, { keyPath: 'cacheKey' });
                productsStore.createIndex('timestamp', 'timestamp', { unique: false });
            }

            // Store per le varianti
            if (!db.objectStoreNames.contains(VARIANTS_STORE)) {
                const variantsStore = db.createObjectStore(VARIANTS_STORE, { keyPath: 'cacheKey' });
                variantsStore.createIndex('timestamp', 'timestamp', { unique: false });
                variantsStore.createIndex('productId', 'productId', { unique: false });
            }

            // Store per i brand
            if (!db.objectStoreNames.contains(BRANDS_STORE)) {
                const brandsStore = db.createObjectStore(BRANDS_STORE, { keyPath: 'id' });
                brandsStore.createIndex('timestamp', 'timestamp', { unique: false });
            }

            // Store per metadati (conteggi, ultimo aggiornamento, ecc.)
            if (!db.objectStoreNames.contains(METADATA_STORE)) {
                const metadataStore = db.createObjectStore(METADATA_STORE, { keyPath: 'key' });
                metadataStore.createIndex('timestamp', 'timestamp', { unique: false });
            }
        };

        request.onsuccess = (event) => {
            const db = event.target.result;
            resolve(db);
        };
    });
};

// Funzione generica per salvare dati nella cache
const saveToCache = async (storeName, data, cacheKey, additionalData = {}) => {
    try {
        const db = await initializeDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            // Sanifica i dati prima del salvataggio
            const sanitizedData = sanitizeDataForCache(data);
            const sanitizedAdditionalData = sanitizeDataForCache(additionalData);

            // Per METADATA_STORE, usa "key" invece di "cacheKey" per essere coerente con keyPath
            const cacheItem = storeName === METADATA_STORE
                ? {
                    ...sanitizedData,
                    key: cacheKey,  // Usa "key" per METADATA_STORE
                    timestamp: Date.now(),
                    ...sanitizedAdditionalData
                }
                : {
                    ...sanitizedData,
                    cacheKey,  // Usa "cacheKey" per gli altri store
                    timestamp: Date.now(),
                    ...sanitizedAdditionalData
                };

            const request = store.put(cacheItem);

            request.onsuccess = () => resolve(true);
            request.onerror = (event) => {
                console.error(`Error saving to ${storeName} cache:`, event.target.error);
                reject(event.target.error);
            };

            transaction.oncomplete = () => db.close();
        });
    } catch (error) {
        console.error(`Failed to save to ${storeName} cache:`, error);
        return false;
    }
};

// Funzione generica per recuperare dati dalla cache
const getFromCache = async (storeName, cacheKey) => {
    try {
        const db = await initializeDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);

            // Per METADATA_STORE, cerchiamo per "key" invece di "cacheKey"
            const request = storeName === METADATA_STORE
                ? store.get(cacheKey)  // In METADATA_STORE, cacheKey è usato direttamente come key
                : store.get(cacheKey); // Negli altri store, cerchiamo per cacheKey

            request.onsuccess = (event) => {
                const result = event.target.result;
                db.close();

                if (!result) {
                    resolve(null);
                    return;
                }

                // Verifica se il dato è scaduto
                const ttl = storeName === PRODUCTS_STORE
                    ? CACHE_TTL.PRODUCTS
                    : storeName === VARIANTS_STORE
                        ? CACHE_TTL.VARIANTS
                        : CACHE_TTL.BRANDS;

                const isExpired = (Date.now() - result.timestamp) > ttl;

                if (isExpired) {
                    // Se è scaduto, rimuovilo dalla cache
                    deleteFromCache(storeName, cacheKey).catch(console.error);
                    resolve(null);
                    return;
                }

                resolve(result);
            };

            request.onerror = (event) => {
                console.error(`Error retrieving from ${storeName} cache:`, event.target.error);
                db.close();
                reject(event.target.error);
            };
        });
    } catch (error) {
        console.error(`Failed to retrieve from ${storeName} cache:`, error);
        return null;
    }
};

// Funzione per eliminare un elemento dalla cache
const deleteFromCache = async (storeName, cacheKey) => {
    try {
        const db = await initializeDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);

            // Per METADATA_STORE, eliminiamo per "key" invece che per "cacheKey"
            const request = store.delete(cacheKey);

            request.onsuccess = () => {
                db.close();
                resolve(true);
            };

            request.onerror = (event) => {
                console.error(`Error deleting from ${storeName} cache:`, event.target.error);
                db.close();
                reject(event.target.error);
            };
        });
    } catch (error) {
        console.error(`Failed to delete from ${storeName} cache:`, error);
        return false;
    }
};

// Funzione per cancellare tutti i dati di uno store
const clearStoreCache = async (storeName) => {
    try {
        const db = await initializeDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.clear();

            request.onsuccess = () => {
                db.close();
                resolve(true);
            };

            request.onerror = (event) => {
                console.error(`Error clearing ${storeName} cache:`, event.target.error);
                db.close();
                reject(event.target.error);
            };
        });
    } catch (error) {
        console.error(`Failed to clear ${storeName} cache:`, error);
        return false;
    }
};

// Funzioni specifiche per la cache dei prodotti
const saveProductsToCache = async (products, page, pageSize, filters = null) => {
    if (!products || products.length === 0) {
        console.warn('Attempting to cache empty products array, skipping');
        return false;
    }

    // Crea un hash dei filtri per includere nella chiave della cache
    const filtersHash = filters ? JSON.stringify(filters) : 'nofilters';

    // Includi i filtri nella chiave della cache
    const cacheKey = `products_${page}_${pageSize}_${filtersHash}`;

    return saveToCache(PRODUCTS_STORE, { products, page, pageSize, filters }, cacheKey);
};

const getProductsFromCache = async (page, pageSize, filters = null) => {
    // Crea un hash dei filtri per includere nella chiave della cache
    const filtersHash = filters ? JSON.stringify(filters) : 'nofilters';

    // Includi i filtri nella chiave della cache
    const cacheKey = `products_${page}_${pageSize}_${filtersHash}`;

    const result = await getFromCache(PRODUCTS_STORE, cacheKey);

    if (result) {
        return result.products;
    } else {
        return null;
    }
};

// Pulisce la cache dei prodotti per tutte le pagine
const clearProductsCache = async () => {
    return clearStoreCache(PRODUCTS_STORE);
};

// Funzioni specifiche per la cache delle varianti
const saveVariantsToCache = async (variants, productId) => {
    const cacheKey = `variants_${productId}`;
    return saveToCache(VARIANTS_STORE, { variants }, cacheKey, { productId });
};

const getVariantsFromCache = async (productId) => {
    const cacheKey = `variants_${productId}`;
    const result = await getFromCache(VARIANTS_STORE, cacheKey);
    return result ? result.variants : null;
};

// Funzioni specifiche per la cache dei brand
const saveBrandsToCache = async (brands) => {
    try {

        // Verifica che i brand abbiano tutti i campi necessari e siano serializzabili
        const validBrands = brands.map((brand, index) => {
            try {
                // Test di serializzazione
                JSON.stringify(brand);

                // Assicurati che il brand abbia un'etichetta valida
                if (!brand.label || brand.label === 'Unnamed Brand') {
                    console.warn(`Brand ${brand.id} has invalid label:`, brand.label);

                    // Cerca di trovare un nome valido nei dati del brand
                    let brandName = null;
                    if (brand.nameBrand) brandName = brand.nameBrand;
                    else if (brand.name) brandName = brand.name;
                    else if (brand.brandName) brandName = brand.brandName;

                    if (brandName) {
                        return {
                            ...brand,
                            label: brandName
                        };
                    }
                }
                return brand;
            } catch (serializationError) {
                console.error(`❌ Brand ${index} (id: ${brand?.id}) cannot be serialized:`, serializationError);
                console.error('Problematic brand data:', brand);

                // Tenta di creare una versione pulita del brand
                const safeBrand = {
                    id: brand.id || `brand_${index}`,
                    label: brand.label || brand.nameBrand || brand.name || 'Unknown Brand',
                    value: brand.value || brand.id || `brand_${index}`
                };
                return safeBrand;
            }
        }).filter(brand => brand !== null);

        // Salva ogni brand individualmente per facilitare gli aggiornamenti
        const promises = validBrands.map((brand, index) => {
            return saveToCache(BRANDS_STORE, brand, brand.id).catch(error => {
                console.error(`❌ Failed to save brand ${brand.id} to cache:`, error);
                console.error('Brand data that failed:', brand);
                throw error;
            });
        });

        const results = await Promise.all(promises);

        return results;

    } catch (error) {
        console.error('❌ Error in saveBrandsToCache:', error);
        throw error;
    }
};

const getBrandsFromCache = async () => {
    try {
        const db = await initializeDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(BRANDS_STORE, 'readonly');
            const store = transaction.objectStore(BRANDS_STORE);
            const request = store.getAll();

            request.onsuccess = (event) => {
                const results = event.target.result;
                db.close();

                // Se non ci sono risultati, restituisci null
                if (!results || results.length === 0) {
                    resolve(null);
                    return;
                }

                // Filtra i brand scaduti
                const validBrands = results.filter(brand => {
                    const isExpired = (Date.now() - brand.timestamp) > CACHE_TTL.BRANDS;
                    if (isExpired) {
                        // Rimuovi i brand scaduti in background
                        deleteFromCache(BRANDS_STORE, brand.id).catch(console.error);
                        return false;
                    }
                    return true;
                });

                // Se non ci sono brand validi dopo il filtraggio, restituisci null
                if (validBrands.length === 0) {
                    resolve(null);
                    return;
                }

                resolve(validBrands);
            };

            request.onerror = (event) => {
                console.error('Error retrieving brands from cache:', event.target.error);
                db.close();
                reject(event.target.error);
            };
        });
    } catch (error) {
        console.error('Failed to retrieve brands from cache:', error);
        return null;
    }
};

// Salva il conteggio totale dei prodotti
const saveProductsCountToCache = async (count) => {
    return saveToCache(METADATA_STORE, { count }, 'totalProductsCount');
};

// Recupera il conteggio totale dei prodotti
const getProductsCountFromCache = async () => {
    const result = await getFromCache(METADATA_STORE, 'totalProductsCount');
    return result ? result.count : null;
};

// Funzione per cancellare tutta la cache
const clearAllCache = async () => {
    await clearStoreCache(PRODUCTS_STORE);
    await clearStoreCache(VARIANTS_STORE);
    await clearStoreCache(BRANDS_STORE);
    await clearStoreCache(METADATA_STORE);
    return true;
};

// Funzioni specifiche per la cache dei prodotti con filtri
const saveProductsWithFiltersToCache = async (products, page, pageSize, filters) => {
    if (!products || products.length === 0) {
        console.warn('Attempting to cache empty products array, skipping');
        return false;
    }

    // Crea un hash dei filtri per includere nella chiave della cache
    const filtersHash = filters ? JSON.stringify(filters) : 'nofilters';

    // Includi i filtri nella chiave della cache
    const cacheKey = `products_${page}_${pageSize}_${filtersHash}`;

    return saveToCache(PRODUCTS_STORE, { products, page, pageSize, filters }, cacheKey);
};

const getProductsWithFiltersFromCache = async (page, pageSize, filters) => {
    // Crea un hash dei filtri per includere nella chiave della cache
    const filtersHash = filters ? JSON.stringify(filters) : 'nofilters';

    // Includi i filtri nella chiave della cache
    const cacheKey = `products_${page}_${pageSize}_${filtersHash}`;

    const result = await getFromCache(PRODUCTS_STORE, cacheKey);

    if (result) {
        return result.products;
    } else {
        return null;
    }
};

// Funzioni per salvare e recuperare prodotti con le loro varianti in un'unica operazione
const saveProductsWithVariantsToCache = async (products, variants, page, pageSize, filters = null) => {
    if (!products || products.length === 0) {
        console.warn('Attempting to cache empty products array, skipping');
        return false;
    }

    // Crea un hash dei filtri per includere nella chiave della cache
    const filtersHash = filters ? JSON.stringify(filters) : 'nofilters';

    // Includi i filtri nella chiave della cache
    const cacheKey = `products_with_variants_${page}_${pageSize}_${filtersHash}`;

    return saveToCache(PRODUCTS_STORE, { products, variants, page, pageSize, filters, withVariants: true }, cacheKey);
};

const getProductsWithVariantsFromCache = async (page, pageSize, filters = null) => {
    // Crea un hash dei filtri per includere nella chiave della cache
    const filtersHash = filters ? JSON.stringify(filters) : 'nofilters';

    // Includi i filtri nella chiave della cache
    const cacheKey = `products_with_variants_${page}_${pageSize}_${filtersHash}`;

    const result = await getFromCache(PRODUCTS_STORE, cacheKey);

    if (result && result.withVariants) {
        return {
            products: result.products,
            variants: result.variants
        };
    } else {
        return null;
    }
};

export {
    saveProductsToCache,
    getProductsFromCache,
    saveVariantsToCache,
    getVariantsFromCache,
    saveBrandsToCache,
    getBrandsFromCache,
    saveProductsCountToCache,
    getProductsCountFromCache,
    clearAllCache,
    clearStoreCache,
    clearProductsCache,
    saveProductsWithFiltersToCache,
    getProductsWithFiltersFromCache,
    saveProductsWithVariantsToCache,
    getProductsWithVariantsFromCache
}; 